import { useMemo } from "react"
import { useInfiniteQuery, useQuery } from "@tanstack/react-query"
import type { DrawerWhySection, OverviewFilters, TopicExampleRow } from "@nexus/contracts"
import {
  myBrandDrawerWhyDataQueryOptions,
  type DrilldownPageFiltersInput,
} from "@/lib/queries/drilldown.queries"
import type { MyBrandFiltersInput } from "@/lib/queries/dashboard.queries"
import { topicDetailExamplesInfiniteQueryOptions } from "@/lib/queries/overview.queries"
import type { TopicDetailContext } from "@/features/overview/lib/topicDetailContext"
import { TOPIC_DETAIL_OR_DELIMITER } from "@/features/overview/lib/buildTopicDetailSearch"
import type { MyBrandCreativeCardModel } from "../lib/drilldown-card.types"

/** "Why it works" tab data — fetched only while the tab is open. */
export function useMyBrandDrawerWhyDataSection(
  creative: MyBrandCreativeCardModel | null,
  open: boolean,
  activeTab: string,
  myBrandFilters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
): {
  section: DrawerWhySection | null
  isLoading: boolean
  isError: boolean
  /** The request came back with no section: nothing to read for this tenant. */
  isUnavailable: boolean
} {
  const hash = creative?.hash ?? ""
  const shouldFetch = open && activeTab === "why" && hash.length > 0

  const { data, isLoading, isError, isSuccess } = useQuery(
    myBrandDrawerWhyDataQueryOptions(hash, myBrandFilters, drilldownFilters, shouldFetch),
  )

  return {
    section: data ?? null,
    isLoading: shouldFetch && isLoading,
    isError,
    isUnavailable: isSuccess && data === null,
  }
}

/**
 * Other own creatives with one tag — reuses the Overview topic-detail Examples route
 * (`search` = the tag's combinations OR-joined, one row per hash, highest spend first).
 */
export function useWhyTagExamples(
  tenantId: string | null,
  combinations: string[] | null,
  myBrandFilters: MyBrandFiltersInput,
  drilldownFilters: DrilldownPageFiltersInput,
): { rows: TopicExampleRow[]; isLoading: boolean; isError: boolean } {
  const overviewFilters = useMemo<OverviewFilters>(
    () => ({
      groupBy: "",
      subFeature: [],
      featureTimeFrame: [],
      objectType: drilldownFilters.objectType,
      effectiveStatus: drilldownFilters.effectiveStatus,
      scenario: [],
      groupByValues: [],
      audienceSegment: drilldownFilters.audienceSegment,
      device: drilldownFilters.device,
      startDate: drilldownFilters.startDate,
      endDate: drilldownFilters.endDate,
      advancedFilters: drilldownFilters.advancedFilters,
      aspectRatio: drilldownFilters.aspectRatio,
    }),
    [drilldownFilters],
  )
  const globalFilters = useMemo(
    () => ({ ...myBrandFilters, startDate: drilldownFilters.startDate, endDate: drilldownFilters.endDate }),
    [myBrandFilters, drilldownFilters.startDate, drilldownFilters.endDate],
  )
  const context = useMemo<TopicDetailContext | null>(
    () =>
      combinations && combinations.length > 0
        ? {
            topicTitle: "",
            topicDescription: "",
            scenario: "",
            featureTimeFrame: "",
            search: combinations.join(TOPIC_DETAIL_OR_DELIMITER),
            segment1: "Hash",
            segment2: "All",
            segment3: "All",
          }
        : null,
    [combinations],
  )

  const { data, isLoading, isError } = useInfiniteQuery(
    topicDetailExamplesInfiniteQueryOptions(tenantId, overviewFilters, globalFilters, context),
  )

  return { rows: data?.pages[0] ?? [], isLoading: !!context && isLoading, isError }
}
